import type { ComposeType, ComposeToolbarType } from '../components/Customers/UnifiedDialog/components/InputTypes/types';

const SELECTED_CUSTOMER_ID_KEY = 'selectedCustomerId';

// ========== Selected Customer ID ==========

/**
 * Gets the selected customer ID from localStorage
 * @returns The saved customer ID or null if not found
 */
export const getSelectedCustomerId = (): number | null => {
  if (typeof window === 'undefined') return null;

  try {
    const stored = localStorage.getItem(SELECTED_CUSTOMER_ID_KEY);
    if (!stored) return null;

    const customerId = parseInt(stored, 10);
    return isNaN(customerId) ? null : customerId;
  } catch (error) {
    console.error('Failed to get selected customer ID from localStorage:', error);
    return null;
  }
};

/**
 * Saves the selected customer ID to localStorage
 * @param customerId - The customer ID to save (or null to clear)
 */
export const setSelectedCustomerId = (customerId: number | null): void => {
  if (typeof window === 'undefined') return;

  try {
    if (customerId === null) {
      localStorage.removeItem(SELECTED_CUSTOMER_ID_KEY);
    } else {
      localStorage.setItem(SELECTED_CUSTOMER_ID_KEY, customerId.toString());
    }
  } catch (error) {
    console.error('Failed to set selected customer ID in localStorage:', error);
  }
};

/**
 * Clears the selected customer from localStorage
 */
export const clearSelectedCustomerId = (): void => {
  setSelectedCustomerId(null);
};

// ========== Customer List Filters ==========

const CUSTOMER_ACTIVE_ONLY_KEY = 'customerActiveOnly';
const CUSTOMER_SEARCH_QUERY_KEY = 'customerSearchQuery';

/**
 * Gets the saved "active only" filter state from localStorage
 * @returns The saved state or true as default
 */
export const getCustomerActiveOnly = (): boolean => {
  if (typeof window === 'undefined') return true;

  try {
    const stored = localStorage.getItem(CUSTOMER_ACTIVE_ONLY_KEY);
    if (stored === null) return true;
    return stored === 'true';
  } catch (error) {
    console.error('Failed to get customer active only from localStorage:', error);
    return true;
  }
};

/**
 * Saves the "active only" filter state to localStorage
 * @param isActiveOnly - The state to save
 */
export const setCustomerActiveOnly = (isActiveOnly: boolean): void => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(CUSTOMER_ACTIVE_ONLY_KEY, isActiveOnly.toString());
  } catch (error) {
    console.error('Failed to set customer active only in localStorage:', error);
  }
};

/**
 * Gets the saved search query from localStorage
 * @returns The saved search query or empty string as default
 */
export const getCustomerSearchQuery = (): string => {
  if (typeof window === 'undefined') return '';

  try {
    const stored = localStorage.getItem(CUSTOMER_SEARCH_QUERY_KEY);
    return stored || '';
  } catch (error) {
    console.error('Failed to get customer search query from localStorage:', error);
    return '';
  }
};

/**
 * Saves the search query to localStorage
 * @param searchQuery - The search query to save
 */
export const setCustomerSearchQuery = (searchQuery: string): void => {
  if (typeof window === 'undefined') return;

  try {
    if (searchQuery) {
      localStorage.setItem(CUSTOMER_SEARCH_QUERY_KEY, searchQuery);
    } else {
      localStorage.removeItem(CUSTOMER_SEARCH_QUERY_KEY);
    }
  } catch (error) {
    console.error('Failed to set customer search query in localStorage:', error);
  }
};

/**
 * Clears all customer list filters from localStorage
 */
export const clearCustomerFilters = (): void => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(CUSTOMER_ACTIVE_ONLY_KEY);
    localStorage.removeItem(CUSTOMER_SEARCH_QUERY_KEY);
  } catch (error) {
    console.error('Failed to clear customer filters from localStorage:', error);
  }
};

// ========== Selected Call ID (for Calls page) ==========

const SELECTED_CALL_ID_KEY = 'selectedCallId';

/**
 * Gets the selected call ID from localStorage
 * @returns The saved call ID or null if not found
 */
export const getSelectedCallId = (): number | null => {
  if (typeof window === 'undefined') return null;

  try {
    const stored = localStorage.getItem(SELECTED_CALL_ID_KEY);
    if (!stored) return null;

    const callId = parseInt(stored, 10);
    return isNaN(callId) ? null : callId;
  } catch (error) {
    console.error('Failed to get selected call ID from localStorage:', error);
    return null;
  }
};

/**
 * Saves the selected call ID to localStorage
 * @param callId - The call ID to save (or null to clear)
 */
export const setSelectedCallId = (callId: number | null): void => {
  if (typeof window === 'undefined') return;

  try {
    if (callId === null) {
      localStorage.removeItem(SELECTED_CALL_ID_KEY);
    } else {
      localStorage.setItem(SELECTED_CALL_ID_KEY, callId.toString());
    }
  } catch (error) {
    console.error('Failed to set selected call ID in localStorage:', error);
  }
};

/**
 * Clears the selected call from localStorage
 */
export const clearSelectedCallId = (): void => {
  setSelectedCallId(null);
};

// ========== Activities Page Filters ==========

const ACTIVITIES_FILTER_KEY = 'activitiesFilter';
const ACTIVITIES_IMPORTANT_ONLY_KEY = 'activitiesImportantOnly';

export type ActivitiesFilterType = 'all' | 'to-do' | 'overdue' | 'today' | 'tomorrow' | 'this-week' | 'next-week' | 'done';

const VALID_ACTIVITIES_FILTERS: ActivitiesFilterType[] = ['all', 'to-do', 'overdue', 'today', 'tomorrow', 'this-week', 'next-week', 'done'];

/**
 * Gets the saved activities filter from localStorage
 * @returns The saved filter or 'today' as default
 */
export const getActivitiesFilter = (): ActivitiesFilterType => {
  if (typeof window === 'undefined') return 'today';

  try {
    const stored = localStorage.getItem(ACTIVITIES_FILTER_KEY);
    if (stored && VALID_ACTIVITIES_FILTERS.includes(stored as ActivitiesFilterType)) {
      return stored as ActivitiesFilterType;
    }
    return 'today';
  } catch (error) {
    console.error('Failed to get activities filter from localStorage:', error);
    return 'today';
  }
};

/**
 * Saves the activities filter to localStorage
 * @param filter - The filter to save
 */
export const setActivitiesFilter = (filter: ActivitiesFilterType): void => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(ACTIVITIES_FILTER_KEY, filter);
  } catch (error) {
    console.error('Failed to set activities filter in localStorage:', error);
  }
};

/**
 * Gets the saved "important only" state for activities from localStorage
 * @returns The saved state or false as default
 */
export const getActivitiesImportantOnly = (): boolean => {
  if (typeof window === 'undefined') return false;

  try {
    const stored = localStorage.getItem(ACTIVITIES_IMPORTANT_ONLY_KEY);
    if (stored === null) return false;
    return stored === 'true';
  } catch (error) {
    console.error('Failed to get activities important only from localStorage:', error);
    return false;
  }
};

/**
 * Saves the "important only" state for activities to localStorage
 * @param isImportantOnly - The state to save
 */
export const setActivitiesImportantOnly = (isImportantOnly: boolean): void => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(ACTIVITIES_IMPORTANT_ONLY_KEY, isImportantOnly.toString());
  } catch (error) {
    console.error('Failed to set activities important only in localStorage:', error);
  }
};

/**
 * Clears all activities filters from localStorage
 */
export const clearActivitiesFilters = (): void => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(ACTIVITIES_FILTER_KEY);
    localStorage.removeItem(ACTIVITIES_IMPORTANT_ONLY_KEY);
  } catch (error) {
    console.error('Failed to clear activities filters from localStorage:', error);
  }
};

// ========== Compose Drafts ==========

const COMPOSE_DRAFT_KEY_PREFIX = 'composeDraft';

const buildDraftKey = (type: ComposeType, customerId: number): string => {
  return `${COMPOSE_DRAFT_KEY_PREFIX}_${type}_${customerId}`;
};

/**
 * Gets a compose draft from localStorage
 * @param type - The compose type (sms, email, internalNote)
 * @param customerId - The customer ID
 * @returns The saved draft or null if not found
 */
export const getComposeDraft = <T>(type: ComposeType, customerId: number): T | null => {
  if (typeof window === 'undefined') return null;

  try {
    const key = buildDraftKey(type, customerId);
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    return JSON.parse(stored) as T;
  } catch (error) {
    console.error(`Failed to get ${type} draft from localStorage:`, error);
    return null;
  }
};

/**
 * Saves a compose draft to localStorage
 * @param type - The compose type (sms, email, internalNote)
 * @param customerId - The customer ID
 * @param draft - The draft to save
 */
export const setComposeDraft = <T>(type: ComposeType, customerId: number, draft: T): void => {
  if (typeof window === 'undefined') return;

  try {
    const key = buildDraftKey(type, customerId);
    localStorage.setItem(key, JSON.stringify(draft));
  } catch (error) {
    console.error(`Failed to set ${type} draft in localStorage:`, error);
  }
};

/**
 * Clears a compose draft from localStorage
 * @param type - The compose type (sms, email, internalNote)
 * @param customerId - The customer ID
 */
export const clearComposeDraft = (type: ComposeType, customerId: number): void => {
  if (typeof window === 'undefined') return;

  try {
    const key = buildDraftKey(type, customerId);
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Failed to clear ${type} draft from localStorage:`, error);
  }
};

// ========== Compose Toolbar Type ==========

const COMPOSE_TYPE_KEY = 'composeToolbarType';

/**
 * Gets the saved compose toolbar type from localStorage
 * @returns The saved compose type or 'Notes' as default
 */
export const getComposeToolbarType = (): ComposeToolbarType => {
  if (typeof window === 'undefined') return 'Notes';

  try {
    const stored = localStorage.getItem(COMPOSE_TYPE_KEY);
    if (stored === 'SMS' || stored === 'Email' || stored === 'Notes' || stored === 'null') {
      return stored === 'null' ? null : stored;
    }
    return 'Notes';
  } catch (error) {
    console.error('Failed to get compose toolbar type from localStorage:', error);
    return 'Notes';
  }
};

/**
 * Saves the compose toolbar type to localStorage
 * @param composeType - The compose type to save (SMS, Email, Notes, or null)
 */
export const setComposeToolbarType = (composeType: ComposeToolbarType): void => {
  if (typeof window === 'undefined') return;

  try {
    const valueToStore = composeType === null ? 'null' : composeType;
    localStorage.setItem(COMPOSE_TYPE_KEY, valueToStore);
  } catch (error) {
    console.error('Failed to set compose toolbar type in localStorage:', error);
  }
};

// ========== Accordion Expansion State ==========

const ACCORDION_EXPANDED_KEY_PREFIX = 'accordionExpanded';

/**
 * Builds the storage key for accordion expansion state
 * @param accordionKey - Unique identifier for the accordion
 * @returns The storage key
 */
const buildAccordionKey = (accordionKey: string): string => {
  return `${ACCORDION_EXPANDED_KEY_PREFIX}_${accordionKey}`;
};

/**
 * Gets the expansion state for an accordion from localStorage
 * @param accordionKey - Unique identifier for the accordion
 * @param defaultValue - Default value if not found (defaults to true)
 * @returns The saved expansion state or default value
 */
export const getAccordionExpanded = (accordionKey: string, defaultValue = true): boolean => {
  if (typeof window === 'undefined') return defaultValue;

  try {
    const key = buildAccordionKey(accordionKey);
    const stored = localStorage.getItem(key);
    if (stored === null) return defaultValue;
    return stored === 'true';
  } catch (error) {
    console.error(`Failed to get accordion expansion state for ${accordionKey} from localStorage:`, error);
    return defaultValue;
  }
};

/**
 * Saves the expansion state for an accordion to localStorage
 * @param accordionKey - Unique identifier for the accordion
 * @param expanded - The expansion state to save
 */
export const setAccordionExpanded = (accordionKey: string, expanded: boolean): void => {
  if (typeof window === 'undefined') return;

  try {
    const key = buildAccordionKey(accordionKey);
    localStorage.setItem(key, expanded.toString());
  } catch (error) {
    console.error(`Failed to set accordion expansion state for ${accordionKey} in localStorage:`, error);
  }
};

